import * as Sentry from '@sentry/vue'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useToast } from '../components/design-system/toaster'
import AnalyticsLayout from '../pages/analytics/Layout.vue'
import CollectionLayout from '../pages/collection/Layout.vue'
import NotFound from '../pages/errors/404.vue'
import ReportsLayout from '../pages/reports/Layout.vue'
import { useTranslationsStore } from '../stores/translationsStore'
import { consumeChunkReloadTarget, hasRecentChunkReload, isChunkLoadError, reloadForChunkError, setPendingNavigationPath } from '../utils/chunkLoadError'
import { setPageTitle } from './composables/usePageTitle'
import { check } from './guards/permission'
import { assetAnalyticsRoutes, assetCollectionRoutes } from './routes/asset'
import { assetGroupCollectionRoutes } from './routes/asset-group'
import { buildingUnitAnalyticsRoutes } from './routes/building-unit'
import { companyAnalyticsRoutes, companyCollectionRoutes } from './routes/company'
import dataRequestRoutes from './routes/data-request'
import { homeRoutes } from './routes/home'
import { portfolioAnalyticsRoutes, portfolioCollectionRoutes } from './routes/portfolio'
import { redirectRoutes } from './routes/redirects'
import { reportsRoutes } from './routes/reports'
import userRoutes, { userProfileRoutes } from './routes/user'

/**
 * New nested routes structure
 * Composes entity routes (company, portfolio, asset) under analytics/collection modes
 */
const mainAppRoutes: RouteRecordRaw = {
    path: '/router',
    component: () => import('@/vue/pages/RootLayout.vue'),
    children: [
        {
            path: '',
            redirect: { name: 'home' },
        },
        // Home (Lumi)
        ...homeRoutes,
        // User Profile
        ...userProfileRoutes,
        // Analytics Mode
        {
            path: 'analytics',
            component: AnalyticsLayout,
            meta: { editMode: false },
            children: [
                {
                    path: '',
                    redirect: { name: 'home' },
                },
                ...companyAnalyticsRoutes,
                ...portfolioAnalyticsRoutes,
                ...assetAnalyticsRoutes,
                ...buildingUnitAnalyticsRoutes,
            ],
        },
        // Collection Mode
        {
            path: 'collection',
            component: CollectionLayout,
            meta: { editMode: true },
            children: [
                {
                    path: '',
                    redirect: { name: 'home' },
                },
                ...companyCollectionRoutes,
                ...portfolioCollectionRoutes,
                ...assetCollectionRoutes,
                ...assetGroupCollectionRoutes,
            ],
        },
        // Reports Mode
        {
            path: 'reports',
            component: ReportsLayout,
            children: [
                {
                    path: '',
                    redirect: { name: 'home' },
                },
                ...reportsRoutes,
            ],
        },
    ],
}

const dataRequestAppRoutes: RouteRecordRaw = {
    path: '/data-request',
    component: () => import('@/vue/data-request/DataRequestLayout.vue'),
    beforeEnter: check([], 'home'),
    children: dataRequestRoutes,
}

const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: '/',
            redirect: '/dashboard',
        },
        mainAppRoutes,
        dataRequestAppRoutes,
        // Auth routes (standalone, no layout)
        ...userRoutes,
        // Redirect routes for backward compatibility
        ...redirectRoutes,
        { path: '/:pathMatch(.*)*', name: 'not-found', component: NotFound },
    ],
    scrollBehavior(to, from, savedPosition) {
        // Exists when Browser's back/forward pressed
        if (savedPosition) {
            return savedPosition
        }

        // When updating URL without changing the component (e.g. tab navigation)
        // We don't want to scroll to the top of the page
        if (from.name === to.name) {
            return {}
        }

        // scroll to top by default
        return { top: 0, left: 0 }
    },
})

// Track pending navigation target for chunk-error recovery
router.beforeEach((to) => {
    setPendingNavigationPath(to.fullPath)
})
router.afterEach(() => {
    setPendingNavigationPath(null)
})

// Set the default page title for each route
router.beforeEach((to) => {
    if (to.meta.title) {
        const { translate } = useTranslationsStore()
        const title = translate(to.meta.title)
        if (title) {
            setPageTitle(title)
            return
        }
    } else if (to.meta.title !== null) {
        setPageTitle()
    }
})

// Handle chunk load errors during navigation (e.g., after a deployment)
router.onError((error, to) => {
    if (!isChunkLoadError(error)) {
        return
    }

    if (!hasRecentChunkReload()) {
        reloadForChunkError(to.fullPath)
    } else {
        Sentry.captureException(error, {
            tags: { error_type: 'chunk_load_failure_persistent' },
            extra: { url: window.location.href },
        })

        const { toast } = useToast()
        toast({
            id: 'chunk-load-error',
            variant: 'info',
            title: 'A new version is available',
            description: 'Please refresh to continue',
            duration: 0,
            closeButton: false,
            action: {
                label: 'Refresh',
                onClick: () => window.location.reload(),
            },
        })
    }
})

// After a chunk-reload, redirect to the page the user originally navigated to
const chunkReloadTarget = consumeChunkReloadTarget()
if (chunkReloadTarget) {
    router.isReady().then(() => router.replace(chunkReloadTarget)).catch(() => {})
}

export default router
